import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { scrollToQuote } from "@/lib/scrollToQuote";

const NAV = [
    { label: "Services", href: "#services" },
    { label: "Portfolio", href: "#portfolio" },
    { label: "About", href: "#about" },
    { label: "Reviews", href: "#reviews" },
    { label: "Contact", href: "#quote" },
];

export default function Nav({ logoUrl, phone }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-nav"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-black/90 backdrop-blur-md border-b border-zinc-900"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
                <a href="#top" className="flex items-center gap-3" data-testid="nav-logo">
                    <img
                        src={logoUrl}
                        alt="Ruiz Concrete"
                        className="h-10 w-10 md:h-12 md:w-12 object-cover"
                    />
                    <div className="leading-tight">
                        <div className="font-display text-xl md:text-2xl text-white">
                            Ruiz Concret3
                        </div>
                        <div className="font-mono-accent text-[9px] text-yellow-400">
                            Colorado Springs · Est. 2019
                        </div>
                    </div>
                </a>

                <nav className="hidden lg:flex items-center gap-10">
                    {NAV.map((n) => (
                        <a
                            key={n.href}
                            href={n.href}
                            data-testid={`nav-link-${n.label.toLowerCase()}`}
                            className="font-mono-accent text-[11px] text-zinc-400 hover:text-yellow-400 transition-colors"
                        >
                            {n.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-4">
                    <a
                        href={`tel:${phone?.replace(/[^\d+]/g, "")}`}
                        data-testid="nav-phone"
                        className="font-mono-accent text-[11px] text-zinc-300 hover:text-yellow-400 flex items-center gap-2"
                    >
                        <Phone className="w-3.5 h-3.5" /> {phone}
                    </a>
                    <a
                        href="#quote"
                        onClick={scrollToQuote}
                        data-testid="nav-cta"
                        className="bg-yellow-400 text-black font-black uppercase tracking-wider px-5 py-3 text-xs hover:bg-yellow-300 transition-colors"
                    >
                        Free Quote
                    </a>
                </div>

                <button
                    className="lg:hidden text-white p-2"
                    onClick={() => setOpen((v) => !v)}
                    data-testid="nav-toggle"
                    aria-label="Toggle menu"
                >
                    {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden bg-black border-t border-zinc-900" data-testid="mobile-menu">
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {NAV.map((n) => (
                            <a
                                key={n.href}
                                href={n.href}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                className="font-mono-accent text-xs text-zinc-300 hover:text-yellow-400"
                            >
                                {n.label}
                            </a>
                        ))}
                        <a
                            href={`tel:${phone?.replace(/[^\d+]/g, "")}`}
                            className="font-mono-accent text-xs text-yellow-400 flex items-center gap-2"
                        >
                            <Phone className="w-4 h-4" /> {phone}
                        </a>
                        <a
                            href="#quote"
                            onClick={(e) => { scrollToQuote(e); setOpen(false); }}
                            className="bg-yellow-400 text-black font-black uppercase tracking-wider px-5 py-3 text-xs text-center"
                        >
                            Free Quote
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
}
